import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaTimes, FaSearch, FaGraduationCap } from "react-icons/fa";
import "./NewProgramSidebar.css";
import mtechPrograms from "../programDetails/mtechPrograms.json";
import onlineMtechPrograms from "../programDetails/onlineMtechPrograms.json";
import mscPrograms from "../programDetails/mscPrograms.json";
import phdPrograms from "../programDetails/phdPrograms.json";

// Build list from JSON data only (hide items without data)
const buildPrograms = () => {
  const items = [];
  const pushItems = (dataset, category, segment) => {
    Object.entries(dataset || {}).forEach(([key, p]) => {
      if (!p || !p.name) return;
      const id = p.id || key;
      items.push({
        id,
        name: p.name,
        category,
        categorySegment: segment,
        description: (p.about && p.about.description) ? p.about.description : ""
      });
    });
  };
  pushItems(mtechPrograms, "M.Tech", "progmtech");
  pushItems(onlineMtechPrograms, "Online M.Tech", "online-mtech");
  pushItems(mscPrograms, "M.Sc", "progms");
  pushItems(phdPrograms, "PhD", "progphd");
  return items;
};

const aggregatedPrograms = buildPrograms();
const aggregatedCategories = [
  "All",
  ...Array.from(new Set(aggregatedPrograms.map(p => p.category)))
];

const NewProgramSidebar = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredPrograms, setFilteredPrograms] = useState(aggregatedPrograms);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const navigate = useNavigate();

  // Filter programs based on search term and category
  useEffect(() => {
    let filtered = aggregatedPrograms;

    // Filter by category
    if (selectedCategory !== "All") {
      filtered = filtered.filter(
        (program) => program.category === selectedCategory,
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (program) =>
          program.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          program.description
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (program.coordinator &&
            program.coordinator
              .toLowerCase()
              .includes(searchTerm.toLowerCase())),
      );
    }

    setFilteredPrograms(filtered);
  }, [searchTerm, selectedCategory]);

  const handleProgramClick = (program) => {
    navigate(`/programs/${program.id}`);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const categories = aggregatedCategories;

  return (
      <div className="new-program-sidebar">
        {/* Header */}
        <div className="new-sidebar-header">
          <div className="new-sidebar-title">
            <FaGraduationCap className="title-icon" />
            <h2>Academic Programs</h2>
          </div>
        </div>

        {/* Search Section */}
        <div className="new-sidebar-search">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search programs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button
                type="button"
                className="clear-search"
                onClick={clearSearch}
                aria-label="Clear search"
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        {/* Category Filter - Compact */}
        <div className="new-sidebar-filters">
          <div className="category-buttons-compact">
            {categories.map((category) => (
              <motion.button
                key={category}
                type="button"
                className={`category-btn-compact ${selectedCategory === category ? "active" : ""}`}
                onClick={() => setSelectedCategory(category)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Programs List */}
        <div className="new-sidebar-content">
          <div className="programs-scroll">
            {filteredPrograms.length > 0 ? (
              filteredPrograms.map((program, index) => (
                <motion.button
                  key={program.id}
                  type="button"
                  className="program-card-compact"
                  onClick={() => handleProgramClick(program)}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.02 }}
                  whileHover={{
                    x: 6,
                    backgroundColor: "rgba(96, 165, 250, 0.1)",
                    borderLeftColor: "#60a5fa",
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="program-title-compact">{program.name}</div>
                </motion.button>
              ))
            ) : (
              <motion.div
                className="no-results"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="no-results-icon">🔍</div>
                <h3>No programs found</h3>
                <p>Try adjusting your search terms or filters</p>
                {searchTerm && (
                  <button
                    type="button"
                    className="clear-all-btn"
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedCategory("All");
                    }}
                  >
                    Clear all filters
                  </button>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </div>
  );
};

export default NewProgramSidebar;
